// Whether a forecast run authored no forecast at all.
//
// A run whose synthesis never reached an accepted report still returns a shape-valid
// payload: a placeholder distribution over the declared outcomes and, for a multi-value
// question, a generated `multi_report` written OVER that placeholder. Rendering either as
// a forecast states a prediction the forecaster never made.

/**
 * True when the producer both failed postprocess AND authored no report.
 *
 * The conjunction is the point, and NEITHER half is sufficient:
 *
 * - `failed` alone has always ALSO meant "trigger-gen errored, the forecast is fine"
 *   (see `reportPointer.js`), so gating on it would hide good forecasts.
 * - A null `report` alone is how some healthy lanes legitimately arrive.
 *
 * `prediction` is deliberately not consulted: healthy multi-value and numeric runs carry
 * it null as a matter of course.
 *
 * Reads BOTH shapes on purpose — the camelCase render source from
 * `reportPayloadToSource`, and the raw snake_case wire payload a live completion carries —
 * because the same question is asked of both. A precomputed `unauthored` wins when present
 * so an overlaid source keeps the answer its pointer read already derived.
 *
 * @param {object|null|undefined} source a render source or a raw completion payload
 * @returns {boolean}
 */
export function runAuthoredNoForecast(source) {
  if (!source || typeof source !== "object") return false;
  if (typeof source.unauthored === "boolean") return source.unauthored;
  const status = source.postprocessStatus ?? source.postprocess_status ?? null;
  if (status !== "failed") return false;
  const report = source.report ?? source.serverContext?.report ?? null;
  return !report;
}
